import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Award, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const commitments = [
    {
      number: '1',
      title: 'Understand Your Needs',
      description: 'We begin by learning about your personal or business situation, identifying potential risks and the type of protection that best fits your goals.'
    },
    {
      number: '2',
      title: 'Compare Coverage Options',
      description: 'Because we work with multiple insurance carriers, we review available options and recommend policies that provide the right balance of protection and value.'
    },
    {
      number: '3',
      title: 'Ongoing Support',
      description: 'Insurance needs can change over time. Our team remains available to review your coverage, answer questions, and make adjustments whenever necessary.'
    }
  ];

  const whoWeServe = [
    'Individuals and families seeking reliable personal protection',
    'Small business owners looking to safeguard their operations',
    'Growing companies managing evolving risks',
    'Independent professionals who require specialized liability coverage'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-20"
        style={{ 
          backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(https://images.unsplash.com/photo-1758519288372-045de3f65147)',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-white mb-6">About Progress Insurance Solutions</h1>
            <p className="text-2xl text-slate-200 font-semibold">
              Insurance Guidance Built on Trust and Experience
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1758519289074-9de36003622b" 
                alt="Professional consultation"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Progress Insurance Solutions was founded with a simple goal: to make insurance easier to understand and more meaningful for the people and businesses we serve.
                </p>
                <p>
                  We know that insurance decisions can feel overwhelming. Policies, coverage limits, exclusions, and changing regulations can make it difficult to know whether you are truly protected. That is why our approach begins with listening. We take the time to understand your situation, your priorities, and the risks that matter most to you.
                </p>
                <p>
                  Our team works closely with individuals, families, and business owners to design coverage strategies that provide reliable protection while remaining practical and cost-effective.
                </p>
                <p>
                  At Progress Insurance Solutions, we believe that good insurance isn't just about policies—it's about building long-term relationships and being a trusted resource whenever our clients need guidance, support, or clarity.
                </p>
                <p className="font-semibold text-slate-900">
                  Whether you are protecting your home, your vehicle, or your business, our goal is simple: to help you move forward with confidence, knowing that what matters most is properly protected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Commitment to Clients</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              At Progress Insurance Solutions, our goal is to make the process of choosing coverage clear and straightforward. Our advisors guide you through each step so you can feel confident in your decisions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {commitments.map((item, index) => (
              <Card key={index} className="border-2 hover:border-[#C9A361] transition-all hover:shadow-xl group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#C9A361] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 group-hover:scale-110 transition-transform">
                    {item.number}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Who We Serve</h2>
              <p className="text-xl text-slate-600 mb-8">
                Progress Insurance Solutions proudly supports a wide range of clients, including:
              </p>
              <ul className="space-y-4">
                {whoWeServe.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="bg-[#F5F1E8] p-2 rounded-full mt-1">
                      <Shield className="w-5 h-5 text-[#C9A361]" />
                    </div>
                    <span className="text-lg text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 mt-8 leading-relaxed">
                Our team works closely with every client to ensure their coverage continues to support their goals as circumstances change.
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e" 
                alt="Diverse professionals"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Speak With an Insurance Advisor Today</h2>
          <p className="text-xl text-slate-300 mb-8">
            Ready to experience insurance guidance built on trust and experience? Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:954-495-2929">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg transition-all">
                Call 954-495-2929
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
